// Shared plain-text "DAILY CREW SCHEDULE" email builder + publish-result
// summary, used by BOTH the desktop Who's Where Publish button
// (src/components/index.js) and the mobile Today tab Publish button
// (src/pages/MobileDashboard.js), so the two surfaces always send the
// identical email and report results the same way. Extracted 2026-07-23 when
// the mobile Publish button was added. The actual send is
// /api/schedule/email-schedule (see emailSchedule() in db.js).
import { isOutOnDate, fmtPhone } from './dateUtils'
import { isSchedulable } from './roster'

// Build { subject, body, dateStr } for a board date.
// dateKey is toDateString() format ("Tue May 06 2026").
export function buildScheduleEmailText({ dateKey, activeJobs, cellData, roster }) {
  const dateObj = new Date(dateKey)
  const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  const subject = 'Manpower Schedule - ' + dateStr

  // Everyone assigned to any job that day (foreman + workers).
  const assignedNames = new Set()
  activeJobs.forEach(j => {
    const c = cellData[j.id]?.[dateKey]
    if (c?.count > 0) {
      ;(c.workers || []).forEach(w => assignedNames.add(w))
      if (c.foreman) assignedNames.add(c.foreman)
    }
  })

  const blocks = activeJobs
    .map(j => ({ j, c: cellData[j.id]?.[dateKey] || { count: 0, foreman: j.foreman || '', task: '', workers: [] } }))
    .filter(({ c }) => c.count > 0)
  const nl = '\n'
  let body = 'DAILY CREW SCHEDULE' + nl
  body += dateStr + nl
  body += '='.repeat(50) + nl + nl
  body += 'SUMMARY' + nl
  body += 'Total workers: ' + blocks.reduce((s, { c }) => s + c.count, 0) + nl
  body += 'Active sites: ' + blocks.length + nl
  const outW = roster.filter(e => isOutOnDate(e, dateObj))
  body += 'Unavailable: ' + outW.length + nl + nl
  const workerJobCount = {}
  blocks.forEach(({ c }) => {
    const allW = c.foreman ? [c.foreman, ...(c.workers || []).filter(w => w !== c.foreman)] : (c.workers || [])
    allW.forEach(w => { workerJobCount[w] = (workerJobCount[w] || 0) + 1 })
  })
  blocks.forEach(({ j, c }) => {
    body += '-'.repeat(50) + nl
    body += j.num + ' - ' + j.name + nl
    if (j.address) body += 'Address: ' + j.address + nl
    if (j.pmName || j.pm) body += 'PM: ' + (j.pmName || j.pm) + nl
    if (j.gc) body += 'GC: ' + j.gc + nl
    body += 'Foreman: ' + c.foreman + nl
    if (c.task) body += 'Task: ' + c.task + nl
    const allW = c.foreman ? [c.foreman, ...(c.workers || []).filter(w => w !== c.foreman)] : (c.workers || [])
    body += 'Workers (' + c.count + '):' + nl
    allW.forEach(wname => {
      const emp = roster.find(e => e.name === wname) || { trade: '', local: '', cellPhone: '', phone: '' }
      const phone = fmtPhone(emp.cellPhone || emp.phone || '')
      const tradeLocal = emp.local || ''
      const isDouble = workerJobCount[wname] > 1
      body += '  ' + wname
      if (wname === c.foreman) body += ' [FOREMAN]'
      if (isDouble) body += ' *** DOUBLE BOOKED ***'
      if (tradeLocal) body += ' - ' + tradeLocal
      if (phone) body += ' - ' + phone
      body += nl
    })
    body += nl
  })
  if (outW.length > 0) {
    body += '-'.repeat(50) + nl
    body += 'UNAVAILABLE' + nl
    outW.forEach(e => { body += '  ' + e.name + nl })
    body += nl
  }
  // Field crew only — office staff are active but never assignable, so listing them under
  // UNASSIGNED would read as "these people were available and nobody used them".
  const unassigned = roster.filter(e => isSchedulable(e) && !assignedNames.has(e.name) && !isOutOnDate(e, dateObj))
  if (unassigned.length > 0) {
    body += '-'.repeat(50) + nl
    body += 'UNASSIGNED (' + unassigned.length + ')' + nl
    unassigned.forEach(e => {
      const phone = e.cellPhone || e.phone || ''
      const tl = e.local || ''
      body += '  ' + e.name
      if (tl) body += ' - ' + tl
      if (phone) body += ' - ' + phone
      body += nl
    })
    body += nl
  }
  body += '-'.repeat(50) + nl
  body += 'Sent from Crew Dispatch'
  return { subject, body, dateStr }
}

// One shared result message for a publish (r = /api/schedule/publish JSON) +
// schedule-email send (se = /api/schedule/email-schedule JSON). Desktop shows
// it as a toast, mobile as an alert. Returns { msg, isError }.
export function summarizePublishAndEmail(r, se, dateStr) {
  const fmFailed = r.foreman_emails_failed || 0
  const pmSent = r.pm_forms_sent || 0
  const pmFailed = r.pm_forms_failed || 0
  const noRecip = (r.form_skipped_no_recipient || []).length
  let msg = `Schedule published for ${dateStr}. ${r.reports_created} created, ${r.reports_updated} updated. Emails: ${r.emails_sent} foreman${pmSent ? `, ${pmSent} sent directly to PM${pmSent === 1 ? '' : 's'} (no foreman email)` : ''}.`
  if (fmFailed) msg += ` ⚠ ${fmFailed} foreman email${fmFailed === 1 ? '' : 's'} failed — check logs.`
  if (pmFailed) msg += ` ⚠ ${pmFailed} PM form email${pmFailed === 1 ? '' : 's'} failed — check logs.`
  if (noRecip) msg += ` ⚠ ${noRecip} job${noRecip === 1 ? '' : 's'} with no foreman email AND no PM — nobody got the form.`
  const submitted = (r.skipped || []).filter(s => s.reason === 'already_submitted')
  const approved = (r.skipped || []).filter(s => s.reason === 'approved')
  if (submitted.length) msg += ` Note: ${submitted.map(s => `${s.job_number} was already submitted by ${s.foreman_name || 'the foreman'}`).join('; ')} — schedule changes may require admin intervention.`
  if (approved.length) msg += ` Skipped (already approved): ${approved.map(s => s.job_number).join(', ')}.`
  // Email failures are already surfaced above; count only OTHER errors here.
  const surfaced = new Set(['email_failed', 'email_exception', 'pm_form_failed', 'pm_form_exception'])
  const otherErrors = (r.errors || []).filter(e => !surfaced.has(e.error))
  if (otherErrors.length) msg += ` ${otherErrors.length} other error(s) — check logs.`
  if (se?.sent) {
    const n = (se.recipients || []).length
    msg += ` Schedule emailed to ${n} office recipient${n === 1 ? '' : 's'}.`
  } else {
    const why = [se?.error, typeof se?.detail === 'string' ? se.detail : ''].filter(Boolean).join(' — ')
    msg += ` ⚠ Schedule email FAILED${why ? ' (' + why + ')' : ''} — send it manually.`
  }
  const isError = Boolean((r.errors || []).length || fmFailed || pmFailed || !se?.sent)
  return { msg, isError }
}
